/**
 * Policy-status helpers for payment-plan rows (Payment plans page, clearance and create-reminder lists).
 *
 * A row carries the policy status as a taxonomy id — `policyStatusCodeId` on BFF DTOs, `policyStatusCodeID`
 * on Azure Search rows (Payment plans page) — and the taxonomy maps it to a code such as
 * "PolicyStatus.CANCELED". Rows of cancelled policies are shown on a light-red row (`rg-cancelled`,
 * via `cancelledRowKeys` of GrecoReactGrid or a cell `className`) so the user sees them before
 * clearing money against them or reminding them.
 */

export const POLICY_STATUS_CANCELLED_CODE = "PolicyStatus.CANCELED";

/** The grid row style (components/grecoreactgrid/styles.css) of a cancelled policy's row. */
export const CANCELLED_ROW_CLASS = "rg-cancelled";

type PolicyStatusTaxonomy = {
  PolicyStatus?: { byId?: { [id: number]: { code?: string } | undefined } };
};

type PolicyStatusRow = {
  policyStatusCodeId?: number | string | null;
  policyStatusCodeID?: number | string | null;
};

/** True when the row's policy status resolves to the cancelled code. */
export const isCancelledPolicyRow = (
  row: PolicyStatusRow | null | undefined,
  taxonomy: PolicyStatusTaxonomy | null | undefined
): boolean => {
  const id = row?.policyStatusCodeId ?? row?.policyStatusCodeID;
  if (id === null || id === undefined || id === "") return false;
  const code = taxonomy?.PolicyStatus?.byId?.[Number(id)]?.code;
  return code === POLICY_STATUS_CANCELLED_CODE;
};

/** The `keyColumn` values of the rows whose policy is cancelled, for GrecoReactGrid's `cancelledRowKeys`. */
export const cancelledPolicyRowKeys = (
  rows: PolicyStatusRow[] | null | undefined,
  taxonomy: PolicyStatusTaxonomy | null | undefined,
  keyColumn: string
): Set<number | string> => {
  const keys = new Set<number | string>();
  (rows ?? []).forEach((row) => {
    if (isCancelledPolicyRow(row, taxonomy)) {
      keys.add((row as { [key: string]: any })[keyColumn]);
    }
  });
  return keys;
};
